
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useLanguage } from '@/context/language-context';

const faqCategories = [
  {
    id: 'account',
    title: 'Account Management',
    faqs: [
      {
        question: 'How do I create an account?',
        answer: 'To create an account, click on the "Sign Up" button in the top right corner of the homepage. Fill in the required information and follow the verification steps to complete your registration.'
      },
      {
        question: 'How can I reset my password?',
        answer: 'You can reset your password by clicking on the "Forgot Password" link on the login page. Enter your email address, and we will send you instructions to reset your password.'
      },
      {
        question: 'Can I have multiple profiles?',
        answer: 'No, each user is allowed only one profile on our platform. Creating multiple accounts is against our terms of service and may result in account suspension.'
      },
      {
        question: 'How do I delete my account?',
        answer: 'To delete your account, go to your account settings and select the "Delete Account" option. Please note that this action is irreversible, and all your data will be permanently removed from our system.'
      }
    ]
  },
  {
    id: 'subscription',
    title: 'Subscriptions & Billing',
    faqs: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept major credit cards including Visa, Mastercard, and American Express. We also support payments through PayPal and some cryptocurrency options for select plans.'
      },
      {
        question: 'How do I cancel my subscription?',
        answer: 'You can cancel your subscription anytime by going to your account settings and selecting "Manage Subscription." Follow the cancellation process to stop your recurring payments.'
      },
      {
        question: 'Will I get a refund if I cancel?',
        answer: 'We do not provide refunds for canceled subscriptions. However, you will continue to have access to premium features until the end of your current billing cycle.'
      },
      {
        question: 'Can I upgrade or downgrade my plan?',
        answer: 'Yes, you can change your subscription plan at any time. Go to your account settings, select "Manage Subscription," and choose the plan that best suits your needs. Changes will take effect on your next billing cycle.'
      }
    ]
  },
  {
    id: 'safety',
    title: 'Safety & Privacy',
    faqs: [
      {
        question: 'How is my personal information protected?',
        answer: 'We employ industry-standard encryption and security measures to protect your personal information. We never share your private data with third parties without your explicit consent. For more details, please review our Privacy Policy.'
      },
      {
        question: 'Can I block unwanted users?',
        answer: 'Yes, you can block any user from contacting you. Visit their profile page and select the "Block User" option. Blocked users will not be able to view your profile or send you messages.'
      },
      {
        question: 'How do you verify user identities?',
        answer: 'We use a combination of verification methods, including email verification and optional ID verification for enhanced security. Verified profiles are marked with a verification badge.'
      },
      {
        question: 'What should I do if I encounter inappropriate behavior?',
        answer: 'If you encounter any form of harassment or inappropriate behavior, please report it immediately using the "Report" button on the user\'s profile or message. Our moderation team reviews all reports and takes appropriate action within 24 hours.'
      }
    ]
  },
  {
    id: 'features',
    title: 'Features & Usage',
    faqs: [
      {
        question: 'What is the difference between basic and premium memberships?',
        answer: 'Basic membership allows you to browse profiles and send limited messages. Premium membership offers additional features including unlimited messaging, profile boosting, viewing who liked your profile, and advanced search filters.'
      },
      {
        question: 'How does the matching algorithm work?',
        answer: 'Our matching algorithm considers various factors including your preferences, location, interests, and behavior on the platform to suggest compatible profiles. The more you use the platform, the more our algorithm learns your preferences.'
      },
      {
        question: 'Can I use the platform on mobile devices?',
        answer: 'Yes, our platform is fully responsive and works on all mobile devices through your web browser. We also offer dedicated mobile apps for iOS and Android devices.'
      },
      {
        question: 'How can I improve my profile visibility?',
        answer: 'To improve your profile visibility, complete all sections of your profile, upload high-quality photos, regularly update your status, and engage actively with the community. Premium users also benefit from additional visibility boosts.'
      }
    ]
  }
];

const Faq = () => {
  const { t } = useLanguage();
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-pink-gradient text-gradient">{t('faq.title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* Search bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input 
              placeholder={t('faq.search_placeholder')}
              className="pl-10 pr-4 py-2 rounded-full border-gray-300 focus:border-purple-500 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
            />
            <Button className="absolute right-1.5 top-1.5 bg-pink-gradient hover:opacity-90 transition-opacity rounded-full px-4 py-1.5 text-sm">
              {t('faq.search_button')}
            </Button>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {faqCategories.map((category) => (
            <div key={category.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h2 className="text-xl font-semibold mb-6 text-purple-800">{category.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {category.faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`${category.id}-item-${index}`}>
                    <AccordionTrigger className="text-left font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('faq.still_questions')}</h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            {t('faq.contact_support')}
          </p>
          <Button className="bg-pink-gradient hover:opacity-90 transition-opacity">
            {t('faq.contact_button')}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default Faq;
